import {
  getDefaultVersionId,
  getVerseByReference,
  getRandomVerse,
  getBookName,
  loadBibleMetadata,
  getVersionById,
} from '../services/bible';
import { CLASSIC_VERSES, CLASSIC_VERSES_COUNT } from '../services/classicVersesData';

export interface DailyVerse {
  text: string;
  reference: string;
  versionName: string;
  bookName: string;
  chapter: string;
  verse: string;
}

const getUserOffset = (): number => {
  const key = 'lumina-verse-offset';
  try {
    const stored = localStorage.getItem(key);
    if (stored) return parseInt(stored, 10);
  } catch {}
  const offset = Math.floor(Math.random() * 10000);
  try { localStorage.setItem(key, String(offset)); } catch {}
  return offset;
};

const getDateSeed = (): number => Math.floor(Date.now() / 86400000) + getUserOffset();

/**
 * Mode 1: Random verse from the full Bible, locale-matched version.
 */
export const getDailyVerseFullBible = async (locale: string, offset = 0): Promise<DailyVerse> => {
  const versionId = getDefaultVersionId(locale);
  const version = getVersionById(versionId);
  const seed = getDateSeed() + offset;
  const v = await getRandomVerse(versionId, seed);
  if (!v) {
    throw new Error(`No verse data available for ${versionId}`);
  }
  const bible = await loadBibleMetadata(versionId);
  const bookName = getBookName(bible, v.Book);
  return {
    text: v.Text,
    reference: `${bookName} ${v.Chapter}:${v.Verse}`,
    versionName: version?.name || versionId,
    bookName,
    chapter: v.Chapter,
    verse: v.Verse,
  };
};

/**
 * Mode 2: Random verse from the classic 1000, locale-matched version.
 */
export const getDailyVerseClassic = async (locale: string, offset = 0): Promise<DailyVerse> => {
  const versionId = getDefaultVersionId(locale);
  const version = getVersionById(versionId);
  const seed = getDateSeed() + offset;
  const ref = CLASSIC_VERSES[seed % CLASSIC_VERSES_COUNT];
  const v = await getVerseByReference(versionId, ref.Book, ref.Chapter, ref.Verse);
  if (!v) {
    console.warn(`Classic verse not found: ${ref.Book} ${ref.Chapter}:${ref.Verse} in ${versionId} — falling back to full Bible`);
    return getDailyVerseFullBible(locale, offset);
  }
  const bible = await loadBibleMetadata(versionId);
  const bookName = getBookName(bible, ref.Book);
  return {
    text: v.Text,
    reference: `${bookName} ${v.Chapter}:${v.Verse}`,
    versionName: version?.name || versionId,
    bookName,
    chapter: v.Chapter,
    verse: v.Verse,
  };
};

/**
 * Mode 3: Manual selection — up to 5 versions compared side by side.
 */
export const getManualComparison = async (
  locale: string,
  book: string,
  chapter: string,
  verse: string,
  versionIds: string[],
): Promise<DailyVerse[]> => {
  const results = await Promise.all(
    versionIds.slice(0, 5).map(async (versionId) => {
      try {
        const v = await getVerseByReference(versionId, book, chapter, verse);
        if (!v) return null;
        const version = getVersionById(versionId);
        const bible = await loadBibleMetadata(versionId);
        const bookName = getBookName(bible, book);
        return {
          text: v.Text,
          reference: `${bookName} ${v.Chapter}:${v.Verse}`,
          versionName: version?.name || versionId,
          bookName,
          chapter: v.Chapter,
          verse: v.Verse,
        } as DailyVerse;
      } catch {
        return null;
      }
    })
  );
  return results.filter((r): r is DailyVerse => r !== null);
};

/**
 * Convenience: fetch daily verse based on mode.
 */
export const fetchDailyVerse = async (
  locale: string,
  mode: 'full' | 'classic',
  offset = 0,
): Promise<DailyVerse> => {
  if (mode === 'classic') {
    return getDailyVerseClassic(locale, offset);
  }
  return getDailyVerseFullBible(locale, offset);
};
